class Wishlist {
  constructor() {
    this.isWishlistPage = false
    this.storageKey = 'm-wishlist-products'
    this.products = []
    this.productNodes = {}
    this.storageAvailable = this.checkStorageAvailable()

    this.pageTemplate = 'page.wishlist'
    this.addedClass = 'added-to-wishlist'
    this.hasItemClass = 'wishlist-has-item'
    this.selectors = {
      // container: '.m-wishlist-page-content__wrapper',
       container: '.m-wishlist-page-content-custom',
      noProducts: '.m-wishlist-no-products',
      wrapper: '.m-wishlist-card',
      productCard: '.m-product-card',
      wishlistButton: '.m-wishlist-button',
      wishlistText: '.m-wishlist-button-text',
      removeButton: '.m-wishlist-button',
      count: '.m-wishlist-count'
    }
    
    // Safe localStorage access with try-catch
    try {
      const stored = this.getFromStorage()
      this.products = Array.from(new Set(Array.from(stored || [])))
    } catch (error) {
      console.warn('Failed to load wishlist from storage:', error)
      this.products = []
    }
    
    this.isWishlistPage = MinimogSettings.template === this.pageTemplate
    this.init()
  }

  checkStorageAvailable() {
    try {
      const test = '__storage_test__'
      localStorage.setItem(test, test)
      localStorage.removeItem(test)
      return true
    } catch (e) {
      // Safari private browsing or storage quota exceeded
      console.warn('localStorage not available - Safari private mode or quota exceeded')
      return false
    }
  }

  getFromStorage() {
    if (!this.storageAvailable) return []
    try {
      const data = localStorage.getItem(this.storageKey)
      return data ? JSON.parse(data) : []
    } catch (error) {
      console.error('Error parsing wishlist from storage:', error)
      return []
    }
  }

  init = async () => {
    if (this.isWishlistPage) {
      await this.renderWishlistPage()
      this.addEventToRemoveButtons()
    }
    this.setWishlistButtonsState()
    this.addEventToWishlistButtons()
    this.updateWishlistCount()
  }

  saveToStorage = () => {
    if (!this.storageAvailable) {
      console.warn('Cannot save wishlist - storage not available')
      return
    }

    try {
      // Validate and clean products array
      this.products = Array.from(new Set(
        this.products.filter(p => typeof p === 'string' && p.trim().length > 0)
      ))
      
      const serialized = JSON.stringify(this.products)
      localStorage.setItem(this.storageKey, serialized)
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        console.error('Wishlist storage quota exceeded on Safari')
      } else {
        console.error('Error saving wishlist to storage:', error)
      }
    }
  }

  addToWishlist(handle) {
    // Validate handle is a non-empty string
    if (typeof handle === 'string' && handle.trim() && this.products.indexOf(handle) === -1) {
      this.products.push(handle.trim())
      this.saveToStorage()
    } else if (!handle) {
      console.warn('Invalid product handle provided to addToWishlist')
    }
  }

  removeFromWishlist(handle) {
    this.products = this.products.filter(hdl => hdl !== handle)
    this.saveToStorage()
  }

  setWishlistButtonsState = () => {
    const buttons = document.querySelectorAll(this.selectors.wishlistButton)
    buttons.forEach(btn => {
      const prodHandle = btn && btn.dataset.productHandle
      if (this.products.indexOf(prodHandle) >= 0 && btn && !btn.classList.contains(this.addedClass)) {
        this.toggleButtonState(btn, true)

        if (this.isWishlistPage) {
          btn.classList.remove(this.selectors.wishlistButton.replace('.', ''))
          btn.classList.add(this.selectors.removeButton.replace('.', ''))
        }
      }
    })
  }

  updateWishlistCount = () => {
    const size = this.products.length
    const countElems = document.querySelectorAll(this.selectors.count);
    [...countElems].forEach(elem => {
      elem.textContent = size
      if (size < 1) {
        elem.classList.add('m:hidden')
      } else {
        elem.classList.remove('m:hidden')
      }
    })
    const method = size ? 'add' : 'remove'
    document.body.classList[method](this.hasItemClass)
  }

  addEventToWishlistButtons = () => {
    let touchStartPos = { x: 0, y: 0 }
    let isSwiping = false

    // Track touch start to detect movement
    if ('ontouchstart' in window) {
      document.addEventListener('touchstart', (e) => {
        touchStartPos = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY
        }
        isSwiping = false
      }, { passive: true })

      document.addEventListener('touchmove', (e) => {
        const moveX = Math.abs(e.touches[0].clientX - touchStartPos.x)
        const moveY = Math.abs(e.touches[0].clientY - touchStartPos.y)
        if (moveX > 10 || moveY > 10) {
          isSwiping = true
        }
      }, { passive: true })
    }

    addEventDelegate({
      selector: this.selectors.wishlistButton,
      handler: (e, btn) => {
        // If the user was swiping, ignore this click event
        if (isSwiping) return

        const productHandle = btn && btn.dataset.productHandle
        if (productHandle) {
          e.preventDefault()
          const active = !btn.classList.contains(this.addedClass)
          this.toggleButtonState(btn, active)
          this.updateWishlistCount()

          document.querySelectorAll(this.selectors.wishlistButton).forEach(btnItem => {
            if (btnItem && btnItem.dataset.productHandle === productHandle && btnItem !== btn) {
              this.toggleButtonState(btnItem, active)
            }
          })
        }
      }
    })
  }

  toggleButtonState = (btn, active) => {
    if (!btn) {
      console.warn('toggleButtonState called with invalid button')
      return
    }

    const productHandle = btn.dataset && btn.dataset.productHandle
    if (!productHandle || typeof productHandle !== 'string') {
      console.warn('Invalid or missing product handle on button')
      return
    }

    const wishlistText = btn.querySelector(this.selectors.wishlistText)

    try {
      if (active) {
        this.addToWishlist(productHandle)
        btn.classList.add(this.addedClass)
      } else {
        this.removeFromWishlist(productHandle)
        btn.classList.remove(this.addedClass)
      }

      if (wishlistText && wishlistText.dataset) {
        const temp = wishlistText.dataset.revertText
        wishlistText.dataset.revertText = wishlistText.textContent
        wishlistText.textContent = temp
      }
    } catch (error) {
      console.error('Error toggling wishlist button state:', error)
    }
  }

  addEventToRemoveButtons = () => {
    addEventDelegate({
      selector: this.selectors.removeButton,
      handler: (e, btn) => {
        e.preventDefault()
        const prod = btn && btn.closest(this.selectors.wrapper)
        prod && prod.remove()

        const productHandle = btn && btn.dataset.productHandle
        if (productHandle) {
          this.removeFromWishlist(productHandle)
          this.updateWishlistCount()
          if (!this.products.length) {
            this.showNoProductsMessage()
          }
        }
      }
    })
  }

  wishlistRemoveButton(prdHandle) {
    const html = `<svg class="m-svg-icon--medium" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>`;
    const button = document.createElement("DIV");
    button.classList.add("m-tooltip", "m-wishlist-remove-button", "m:block", "md:m:hidden");
    button.setAttribute("data-product-handle", prdHandle);
    button.innerHTML = html;
    return button;
  }

  renderWishlistPage = async () => {
    const container = document.querySelector(this.selectors.container);
    if (container) {
      let noItemAvailable = true
      if (this.products.length) {
        const promises = this.products.map(async hdl => {
          try {
            if (!hdl || typeof hdl !== 'string') {
              console.warn('Invalid product handle:', hdl)
              return
            }
            
            const url = formatUrl('products', hdl, 'view=grid-card-item')
            const prodHTML = await fetchCache(url)
            
            if (!prodHTML) {
              console.warn('No HTML returned for product:', hdl)
              return
            }
            
            const item = document.createElement("DIV");
            // item.classList.add("m:hidden", "m:column", "m-wishlist-card");
            item.classList.add("md:m:w-4/12", "m:w-6/12", "m-product-item");
            item.innerHTML = prodHTML;
            // console.log(prodHTML);
            const customPdCard = item.querySelector(this.selectors.productCard);
            if (customPdCard) {
              customPdCard.classList.add('customPdCard');
            }
            if (item.querySelector(this.selectors.productCard)) {
              noItemAvailable = false
              item.appendChild(this.wishlistRemoveButton(hdl));
              this.productNodes[hdl] = item
            }
          } catch (error) {
            console.error('Error fetching product for wishlist:', hdl, error)
          }
        })

        await Promise.allSettled(promises)

        // Render in order
        this.products.forEach(hdl => {
          const prod = this.productNodes[hdl]
          if (prod) {
            container.appendChild(prod)
            // slider Logic for product card
            this.initPcardSliders(prod);
            if (MinimogTheme.CompareProduct) {
              MinimogTheme.CompareProduct.setCompareButtonsState();
            }

            prod.classList.remove('m:hidden');
          }
        })
      }

      if (noItemAvailable) {
        this.showNoProductsMessage()
      } else {
        this.setWishlistButtonsState()
      }

      container.classList.add('is-visible')
    }
  }

  showNoProductsMessage = () => {
    const container = document.querySelector(this.selectors.container)
    const noProducts = document.querySelector(this.selectors.noProducts)

    container.classList.add('m:hidden')
    noProducts.classList.remove('m:hidden')
  }

  //Logic for handling slider on wishlist page
  initPcardSliders = (root = document) => {
  // mobile only
  if (window.matchMedia('(min-width: 750px)').matches) return;

  root.querySelectorAll('.pcard-mobile-slider').forEach((slider) => {
    // prevent double binding
    if (slider.dataset.pcardInited === '1') return;
    slider.dataset.pcardInited = '1';

    const viewport = slider.querySelector('[data-pcard-viewport]');
    const dotsWrap = slider.querySelector('[data-pcard-indicators]');
    if (!viewport || !dotsWrap) return;

    const dots = Array.from(dotsWrap.querySelectorAll('.pcard-indicator'));
    const slides = Array.from(viewport.querySelectorAll('[data-pcard-slide]'));
    if (!dots.length || !slides.length) return;

    const setActive = (index) => {
      dots.forEach((d) => d.classList.remove('is-active'));
      if (dots[index]) dots[index].classList.add('is-active');
    };

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const idx = Number(dot.getAttribute('data-pcard-dot'));
        const target = slides[idx];
        if (target) target.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
        setActive(idx);
      });
    });

    let ticking = false;
    viewport.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;

        requestAnimationFrame(() => {
          const viewportCenter = viewport.scrollLeft + viewport.clientWidth / 2;

          let bestIndex = 0;
          let bestDist = Infinity;

          slides.forEach((slide, i) => {
            const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
            const dist = Math.abs(slideCenter - viewportCenter);
            if (dist < bestDist) {
              bestDist = dist;
              bestIndex = i;
            }
          });

          setActive(bestIndex);
          ticking = false;
        });
      },
      { passive: true }
    );
  });
};

}

MinimogTheme.Wishlist = new Wishlist()